import React, { useState, useEffect, useCallback } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectPayments } from '@stripe/react-connect-js';
import { createAccountSession, getConnectedAccount, getPaymentHistory } from '../../api/stripeConnect';
import config from '../../config';
// import { loadStripe } from '@stripe/stripe-js';
// import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
// import PaymentForm from './PaymentForm';






interface PaymentProcessingProps {
  communityId?: string;
  userId?: string;
  onClose?: () => void;
  enableRefunds?: boolean;
  enableDisputes?: boolean;
  enableCapturePayments?: boolean;
  enableDestinationChargeManagement?: boolean;
  hideHeader?: boolean;
}

const PaymentProcessing: React.FC<PaymentProcessingProps> = ({ 
  communityId, 
  userId, 
  onClose,
  enableRefunds = true,
  enableDisputes = true,
  enableCapturePayments = true,
  enableDestinationChargeManagement = true,
  hideHeader = false
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  // const [clientSecret, setClientSecret] = useState<string>('');
  // const [accountId, setAccountId] = useState<string>('');
  const [stripeConnectInstance, setStripeConnectInstance] = useState<any>(null);

  useEffect(() => {
    const initializePayments = async () => {
      // Guard: only proceed in secure contexts
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        setError('Payment features require a secure context (HTTPS or localhost).');
        setIsLoading(false);
        return;
      }

      // If communityId and userId are provided, use them to get the account
      if (communityId && userId) {
        try {
          // Get the connected account for this community and user
          const accountData = await getConnectedAccount(communityId, userId);
          if (!accountData || !accountData.accountId) {
            setError('No connected account found for this community and user');
            setIsLoading(false);
            return;
          }

          // setAccountId(accountData.accountId);

          // Create AccountSession with payments component enabled
          const sessionData = await createAccountSession({
            accountId: accountData.accountId,
            components: {
              payments: {
                enabled: true,
                features: {
                  refund_management: enableRefunds,
                  dispute_management: enableDisputes,
                  capture_payments: enableCapturePayments,
                  destination_on_behalf_of_charge_management: enableDestinationChargeManagement,
                },
              },
            },
          });

          // Render only if the component is enabled in the session
          const isEnabled = sessionData?.components?.payments?.enabled === true;
          if (!isEnabled) {
            setError('Payments dashboard is not enabled for this account.');
            setIsLoading(false);
            return;
          }

          // Determine publishable key
          const publishableKey = config.stripePublishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
          if (!publishableKey) {
            setError('Stripe publishable key is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your environment.');
            setIsLoading(false);
            return;
          }

          // Initialize Connect.js
          const connectInstance = await loadConnectAndInitialize({
            publishableKey,
            fetchClientSecret: async () => {
              const fresh = await createAccountSession({
                accountId: accountData.accountId,
                components: {
                  payments: {
                    enabled: true,
                    features: {
                      refund_management: enableRefunds,
                      dispute_management: enableDisputes,
                      capture_payments: enableCapturePayments,
                      destination_on_behalf_of_charge_management: enableDestinationChargeManagement,
                    },
                  },
                },
              });
              return fresh.client_secret;
            },
            appearance: {
              variables: {
                colorPrimary: '#3b82f6',
                colorBackground: '#ffffff',
                colorText: '#1f2937',
                colorDanger: '#ef4444',
                fontFamily: 'system-ui, sans-serif',
                spacingUnit: '8px',
                borderRadius: '8px',
              },
            },
          });

          setStripeConnectInstance(connectInstance);

          setIsLoading(false);
        } catch (err) {
          console.error('Error initializing payments:', err);
          
          // Handle different types of errors
          if (err instanceof Error) {
            if (err.message.includes('Account ID')) {
              setError('Invalid account configuration. Please contact support.');
            } else if (err.message.includes('Failed to create')) {
              setError('Unable to create payment session. Please try again.');
            } else if (err.message.includes('network')) {
              setError('Network error. Please check your connection and try again.');
            } else {
              setError(err.message);
            }
          } else {
            setError('An unexpected error occurred. Please try again.');
          }
          setIsLoading(false);
        }
      } else {
        // If no communityId/userId provided, show error
        setError('Community ID and User ID are required to access payment dashboard');
        setIsLoading(false);
      }
    };

    initializePayments();

    // Cleanup function
    return () => {
      setStripeConnectInstance(null);
    };
  }, [communityId, userId, enableRefunds, enableDisputes, enableCapturePayments, enableDestinationChargeManagement]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Payments</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="mt-4 bg-red-100 text-red-800 px-4 py-2 rounded hover:bg-red-200"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${hideHeader ? 'pt-0' : ''}`}>
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Payment Dashboard</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
      
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className={`p-4 border-b bg-gray-50 ${hideHeader ? 'pt-6' : ''}`}>
          <p className="text-sm text-gray-600">
            Manage your payments, refunds, and disputes directly from this dashboard.
          </p>
        </div>
        
        {/* Stripe Connect Payments via React components */}
        <div className="min-h-[600px] mt-2 mx-3 pt-2">
          {stripeConnectInstance && (
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              <ConnectPayments />
            </ConnectComponentsProvider>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Available Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-700">
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>View all payments and transactions</span>
          </div>
          {enableRefunds && (
            <div className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              <span>Process refunds</span>
            </div>
          )}
          {enableDisputes && (
            <div className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              <span>Manage disputes</span>
            </div>
          )}
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>Export payment data</span>
          </div>
          {enableCapturePayments && (
            <div className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              <span>Capture pending payments</span>
            </div>
          )}
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>View payment details</span>
          </div>
          {enableDestinationChargeManagement && (
            <div className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              <span>Manage destination charges</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};





export const PaymentStatus: React.FC<PaymentProcessingProps> = ({ communityId, userId }) => {
  const [, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [accountId, setAccountId] = useState<string>('');
  const [accountData, setAccountData] = useState<any>(null);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  // const [showPaymentForm, setShowPaymentForm] = useState(false);

  const handleGetConnectedAccount = useCallback(async () => {
    if (!communityId || !userId) return;
    setIsLoading(true);
    setError('');
    
    try {
      const data = await getConnectedAccount(communityId, userId);
      if (data) {
        setAccountId(data.accountId);
        setAccountData(data.accountData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get account');
    } finally {
      setIsLoading(false);
    }
  }, [communityId, userId]);

  const handleGetPaymentHistory = useCallback(async () => {
    if (!accountId) return;

    try {
      const data = await getPaymentHistory(accountId);
      setPaymentHistory(data.payments || []);
    } catch (err) {
      console.error('Failed to get payment history:', err);
    }
  }, [accountId]);

  // const handlePaymentSuccess = (paymentIntent: any) => {
  //   setPaymentHistory(prev => [paymentIntent, ...prev]);
  //   setShowPaymentForm(false);
  //   setError('');
  // };

  // const handlePaymentError = (error: string) => {
  //   setError(error);
  // };

  useEffect(() => {
    handleGetConnectedAccount();
  }, [handleGetConnectedAccount]);

  useEffect(() => {
    handleGetPaymentHistory();
  }, [handleGetPaymentHistory]);

  return (
    <div className="space-y-6">

      {!accountId && !accountData && 
        (
          <>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold mb-2">Payment Processing</h2>
              <p className="text-sm text-gray-600">
                Accept payments directly to connected accounts with platform fees.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                No payment account yet
              </div>
            )}
            {/* <button
              onClick={handleGetConnectedAccount}
              disabled={isLoading}
              className="w-full bg-primary-300 text-white py-2 px-4 rounded hover:bg-primary-400 disabled:opacity-50"
            >
              {isLoading ? 'Loading Account...' : 'Get Connected Account'}
            </button> */}
          </>
        )
      }


      <div className="space-y-4">
        {/* {accountId && (
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-700">
              Account found! Account ID: {accountId}
            </p>
          </div>
        )} */}

        {/* Account Status for Payments */}
        {accountData && (
          <div className="bg-white border rounded-lg p-4 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Payment Account Status</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Charges Enabled:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  accountData.charges_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {accountData.charges_enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Card Payments:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  accountData.capabilities?.card_payments === 'active' ? 'bg-green-100 text-green-800' :
                  accountData.capabilities?.card_payments === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {accountData.capabilities?.card_payments || 'inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Account Type:</span>
                <span className="text-sm text-gray-900">{accountData.controller?.stripe_dashboard?.type || 'Unknown'}</span>
              </div>
            </div>

            {!accountData.charges_enabled && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ Your account needs to be activated before you can accept payments. 
                  Complete the onboarding process first.
                </p>
              </div>
            )}
          </div>
        )}

        {/* {accountId && accountData?.charges_enabled && (
          <button
            onClick={() => setShowPaymentForm(!showPaymentForm)}
            className="w-full bg-primary-300 text-white py-2 px-4 rounded hover:bg-primary-400"
          >
            {showPaymentForm ? 'Hide Payment Form' : 'Show Payment Form'}
          </button>
        )} */}

        {accountId && !accountData?.charges_enabled && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">
              ❌ Payment processing is not available. Please complete your account onboarding first.
            </p>
          </div>
        )}
      </div>

      {/* {showPaymentForm && accountId && (
        <div className="border rounded-lg p-6 bg-gray-50">
          <h3 className="text-lg font-semibold mb-4">Process Payment</h3>
          <Elements stripe={stripePromise}>
            <PaymentForm
              accountId={accountId}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          </Elements>
        </div>
      )} */}

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Payment History</h3>
          <div className="space-y-2">
            {paymentHistory.map((payment, index) => (
              <div key={index} className="bg-white border rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">Payment ID: {payment.id}</p>
                    <p className="text-sm text-gray-600">
                      Amount: ${(payment.amount / 100).toFixed(2)} {payment.currency?.toUpperCase()}
                    </p>
                    <p className="text-sm text-gray-600">
                      Status: {payment.status}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded text-xs ${
                      payment.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                      payment.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {payment.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Features Overview */}
      <div className="mt-8 bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Payment Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              <span>Direct charges to connected accounts</span>
            </div>
            <div className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              <span>Automatic platform fee collection</span>
            </div>
            <div className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              <span>Secure card processing</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              <span>Real-time payment status</span>
            </div>
            <div className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              <span>Payment history tracking</span>
            </div>
            <div className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              <span>Automatic payouts to connected accounts</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



export default PaymentProcessing; 